import { Inject, Injectable, Optional } from '@angular/core';
import { FeatureActivatedGuardWrapper } from '../../guards/feature-activated/feature-activated.guard';
import { first, forkJoin, Observable, of, switchMap } from 'rxjs';
import { User } from '../../state/auth/types';
import { Customer } from '../../models/customer';
import { OrderListComponent } from '../../components/order-list/order-list.component';
import { InvoiceListComponent } from '../../components/invoice-list/invoice-list.component';
import { OfferListComponent } from '../../components/offer-list/offer-list.component';
import { NavigationConfig } from '../../models/navigation-config';
import { ActivityInternalListComponent } from '../../components/activity-internal/activity-internal-list/activity-internal-list.component';
import { CustomerOrderAnalyticsComponent } from '../../components/customer-order-analytics/customer-order-analytics.component';
import { CustomerDocumentsListComponent } from '../../components/customer-documents-list/customer-documents-list.component';
import { CustomerDetailsInfoTabComponent } from '../../components/customer-details-info-tab/customer-details-info-tab.component';
import { NavbarConfigService } from '../navbar-config/navbar-config.service';
import { CustomerOrderFinderComponent } from '../../components/customer-order-finder/customer-order-finder.component';
import { CustomerUserListComponent } from '../../components/customer-user-list/customer-user-list.component';

export interface CustomerDetailsTab {
  id: string,
  name: string,
  component: any,
  icon?: string,
  componentInputParams?: { [s: string]: any },
  sort?: number
}

@Injectable({
  providedIn: 'root'
})
export class CustomerDetailsConfigService {
  constructor(
    protected featureActivatedGuardWrapper: FeatureActivatedGuardWrapper,
    @Optional()
    @Inject('environment')
    protected environment: NavigationConfig,
    protected navbarConfigService: NavbarConfigService
  ) {}

  getCustomerDetailsGungTabs(customer: Customer, roles: string[], user?: User): Observable<CustomerDetailsTab[]> {
    const isUser: boolean = roles.indexOf('USER') !== -1 && roles.indexOf('ADMIN') === -1 && roles.indexOf('SALES') === -1;
    const isSalesOrAdmin: boolean = roles.indexOf('ADMIN') !== -1 || roles.indexOf('SALES') !== -1;

    return this.featureActivatedGuardWrapper.isActivated('gung', '3.0.25', true).pipe(
      switchMap(documentsFeatureActivated => forkJoin({
        documentsFeatureActivated: of(documentsFeatureActivated),
        activitiesFeatureActivated: this.featureActivatedGuardWrapper.isActivated('gung-activities-internal', '3.0.13').pipe(first())
      })),
      switchMap(({documentsFeatureActivated, activitiesFeatureActivated}) => {
        const tabs: CustomerDetailsTab[] = [];

        tabs.push({
          id: 'tab-info',
          name: 'ADDRESS',
          component: CustomerDetailsInfoTabComponent,
          icon: 'fa-solid fa-address-card',
          componentInputParams: { customer: customer },
          sort: 100
        });

        tabs.push({
          id: 'tab-orders',
          name: 'ORDERS',
          component: OrderListComponent,
          icon: 'fa-solid fa-bags-shopping',
          sort: 200
        });

        tabs.push({
          id: 'tab-invoices',
          name: 'INVOICES',
          component: InvoiceListComponent,
          icon: 'fa-solid fa-file-invoice',
          sort: 300
        });

        if (!isUser && this.navbarConfigService?.enableOffers) {
          tabs.push({
            id: 'tab-offers',
            name: 'OFFERS',
            component: OfferListComponent,
            icon: 'fa-regular fa-briefcase',
            sort: 400
          });
        }

        if (isSalesOrAdmin && (this.navbarConfigService?.enableCustomerActivities || activitiesFeatureActivated)) {
          tabs.push({
            id: 'tab-activities',
            name: 'ACTIVITIES',
            component: ActivityInternalListComponent,
            icon: 'fa-solid fa-handshake',
            componentInputParams: { customerId: customer.id, topFilter: true },
            sort: 500
          });
        }

        if (!isUser) {
          tabs.push({
            id: 'tab-order-analytics',
            name: 'ORDER_ANALYTICS',
            component: CustomerOrderAnalyticsComponent,
            icon: 'fa-solid fa-chart-mixed',
            sort: 600
          });
        }

        tabs.push({
          id: 'tab-order-finder',
          name: 'ORDER_FINDER',
          component: CustomerOrderFinderComponent,
          icon: 'fa-solid fa-file-magnifying-glass',
          sort: 610
        });

        if (isSalesOrAdmin && documentsFeatureActivated) {
          tabs.push({
            id: 'tab-documents',
            name: 'DOCUMENTS',
            component: CustomerDocumentsListComponent,
            icon: 'fa-solid fa-file',
            sort: 700
          });
        }

        tabs.push({
          id: 'tab-connected-users',
          name: 'CONNECTED_USERS',
          component: CustomerUserListComponent,
          icon: 'fa-solid fa-user',
          sort: 800
        });

        return of(tabs);
      })
    );
  }
}
